// Iter 88q — ResumesTable extracted from JobBoard.jsx.
//
// Responsibilities:
//   • Render the resume grid (sortable, drag-to-reorder, column show/hide).
//   • Own the visible-row-level actions (comments toggle, edit, download,
//     reject/restore, delete, AI score, UC rank inline edit).
//   • Delegate every mutation to callbacks so the parent controls state.
//
// Every UI-changing action fires a callback prop — this component stays
// stateless except for the memoised sort output.
import React, { useMemo } from "react";
import {
  Loader2, Sparkles, Eye, EyeOff, Columns3, GripVertical, ArrowUpDown,
  RotateCcw, FileText, MessageSquare, Pencil, Download, RefreshCw, XCircle,
  Trash2,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { CommentsPanel } from "@/pages/jobboard/CommentsPanel";
import { fmt, fmtSize } from "@/pages/jobs/utils";
import { RESUME_STATUS, statusMeta } from "@/pages/jobs/resumeStatus";

// Iter 87: colour the AI Score pill by band.
const scoreTone = (v) => {
  if (v == null) return "bg-slate-100 text-slate-500";
  if (v >= 80)   return "bg-emerald-100 text-emerald-800 border border-emerald-200";
  if (v >= 60)   return "bg-sky-100      text-sky-800      border border-sky-200";
  if (v >= 40)   return "bg-amber-100    text-amber-800    border border-amber-200";
  return          "bg-rose-100     text-rose-800     border border-rose-200";
};

export function ResumesTable({
  resumes, loading, filter, onClearFilter, onSetStatus,
  onDownload, onDelete, onScore, onScoreDetail,
  onSendPark, parkBusy, openComments, onToggleComments,
  onReject, onRestore, onEditResume, onRankChange, rankBusy,
  showRejected, rejectedCount, onToggleShowRejected,
  visibleCols, allCols, hiddenCols, onToggleCol, onResetCols,
  sort, onToggleSort, dragKey, onDragStart, onDrop,
  search, onSearch, onPreview,
}) {
  // Iter 87b: apply the user's persisted sort to the visible rows.
  const sortedResumes = useMemo(() => {
    if (!sort?.key) return resumes;
    const col = allCols.find((c) => c.key === sort.key);
    if (!col?.sortKey) return resumes;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...resumes].sort((a, b) => {
      const av = col.sortKey(a); const bv = col.sortKey(b);
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * dir;
    });
  }, [resumes, sort, allCols]);

  const renderCell = (colKey, r) => {
    switch (colKey) {
      case "candidate": return (
        <div>
          <div className="font-medium text-slate-900">{r.candidate_name || <span className="text-slate-400">— unknown —</span>}</div>
          <div className="text-xs text-slate-500">
            {r.candidate_email || <span className="text-slate-300">no email</span>}
            {r.candidate_phone && <> · {r.candidate_phone}</>}
          </div>
        </div>
      );
      case "status": {
        const meta = statusMeta(r.status); const Icon = meta.icon;
        return (
          <Select value={r.status} onValueChange={(v) => onSetStatus(r.resume_id, v)}>
            <SelectTrigger data-testid={`resume-status-select-${r.resume_id}`}
                           className={`w-[140px] h-8 text-xs ${meta.className} border`}>
              <span className="inline-flex items-center gap-1"><Icon className="h-3 w-3" /><SelectValue /></span>
            </SelectTrigger>
            <SelectContent>
              {RESUME_STATUS.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      case "ai_score":
        return r.latest_score != null ? (
          <button type="button" onClick={() => onScoreDetail(r)}
                  data-testid={`resume-score-badge-${r.resume_id}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold transition hover:opacity-80 ${scoreTone(r.latest_score)}`}
                  title="Click to see the AI breakdown">
            {r.latest_score}
          </button>
        ) : (
          <button type="button" onClick={() => onScore(r.resume_id, false)}
                  data-testid={`resume-score-run-${r.resume_id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-500 hover:border-primary hover:text-primary">
            <Sparkles className="h-3 w-3" /> Score
          </button>
        );
      case "uc_rank": return (
        <div className="inline-flex items-center gap-1">
          <input type="number" inputMode="numeric" defaultValue={r.uc_rank ?? ""}
                 onBlur={(e) => {
                   const v = e.target.value;
                   const cur = r.uc_rank ?? "";
                   if (String(v) !== String(cur)) onRankChange(r.resume_id, v);
                 }}
                 onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                 data-testid={`resume-uc-rank-${r.resume_id}`}
                 placeholder="—"
                 className="w-16 h-7 rounded border border-slate-200 bg-white px-2 text-xs text-center focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                 aria-label="UC Rank" />
          {rankBusy?.[r.resume_id] && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
        </div>
      );
      case "file": return (
        <button type="button"
                onClick={() => {
                  // Iter 88j: PDFs open in a new tab (Chrome refuses to
                  // render PDFs inside nested iframes with its "This page
                  // has been blocked by Chrome" screen). For DOCX/TXT the
                  // modal still opens with the extracted text_full inline.
                  const fn = (r.filename || "").toLowerCase();
                  const isPdf = fn.endsWith(".pdf") || (r.content_type || "").includes("pdf");
                  if (isPdf) {
                    window.open(
                      `${api.defaults.baseURL}/jobs/resumes/${r.resume_id}/preview`,
                      "_blank", "noopener");
                  } else {
                    onPreview && onPreview(r);
                  }
                }}
                data-testid={`resume-preview-${r.resume_id}`}
                className="group text-left hover:text-primary transition-colors">
          <div className="text-xs font-medium text-slate-700 group-hover:text-primary underline decoration-slate-200 group-hover:decoration-primary underline-offset-4 decoration-dotted truncate max-w-[220px]">
            {r.filename}
          </div>
          <div className="text-[10px] text-slate-400">{fmtSize(r.size_bytes)} · {r.text_chars} chars · click to preview</div>
        </button>
      );
      case "uploaded": return (
        <div>
          <div className="text-xs text-slate-600">{fmt(r.created_at)}</div>
          <div className="text-[10px] text-slate-400">by {r.uploaded_by_name || "—"}</div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div>
      {filter && (
        <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-800"
             data-testid="resumes-filter-banner">
          <span>Filtering: <b>{filter}</b></span>
          <button onClick={onClearFilter} data-testid="resumes-filter-clear"
                  className="ml-auto rounded bg-white px-2 py-0.5 text-[10px] font-medium hover:bg-amber-100 transition">
            Clear filter
          </button>
        </div>
      )}
      {/* Iter 87b/c: controls row — search box, rejected toggle, columns dropdown. */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-6 py-2 text-xs">
        <div className="relative">
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search name, email, filename, resume body…"
            data-testid="resumes-search"
            className="h-7 w-72 pl-7 text-xs" />
          <FileText className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        </div>
        <button type="button" onClick={onToggleShowRejected}
                data-testid="resumes-toggle-rejected"
                disabled={!rejectedCount && !showRejected}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-medium transition ${
                  showRejected
                    ? "border-rose-300 bg-rose-100 text-rose-800 hover:bg-rose-200"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                }`}>
          {showRejected ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showRejected ? "Hide rejected" : `Show rejected (${rejectedCount})`}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" data-testid="resumes-columns-btn"
                    className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 font-medium text-slate-600 hover:bg-slate-100">
              <Columns3 className="h-3 w-3" /> Columns
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide">Show / hide</DropdownMenuLabel>
            {allCols.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                checked={!hiddenCols.includes(c.key)}
                disabled={c.alwaysOn}
                onCheckedChange={() => !c.alwaysOn && onToggleCol(c.key)}
                data-testid={`resumes-col-toggle-${c.key}`}
              >
                {c.label}
                {c.alwaysOn && <span className="ml-auto text-[10px] text-slate-400">locked</span>}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onResetCols} data-testid="resumes-col-reset">
              <RotateCcw className="h-3.5 w-3.5" /> Reset columns
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="ml-auto text-slate-500">
          {sortedResumes.length} candidate{sortedResumes.length === 1 ? "" : "s"}
          {showRejected ? " · rejected included" : ""}
          {search ? ` · filter: "${search}"` : ""}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="resumes-table">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              {visibleCols.map((c) => (
                <th key={c.key}
                    draggable={!c.alwaysOn}
                    onDragStart={() => onDragStart(c.key)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(c.key)}
                    className={`select-none px-3 py-2 font-medium ${c.align === "center" ? "text-center" : c.align === "right" ? "text-right" : "text-left"} ${dragKey === c.key ? "opacity-40" : ""}`}
                    data-testid={`resumes-th-${c.key}`}>
                  <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : c.align === "center" ? "justify-center" : ""}`}>
                    {!c.alwaysOn && <GripVertical className="h-3 w-3 cursor-grab text-slate-300" />}
                    <span className={c.sortable ? "cursor-pointer hover:text-slate-700" : ""}
                          onClick={() => c.sortable && onToggleSort(c.key)}
                          data-testid={c.sortable ? `resumes-sort-${c.key}` : undefined}>
                      {c.label}
                      {c.sortable && sort.key === c.key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                    </span>
                    {c.sortable && sort.key !== c.key && <ArrowUpDown className="h-3 w-3 opacity-30" />}
                  </span>
                </th>
              ))}
              {/* Iter 88i: sticky-right Actions column — was getting cut off
                  when the horizontal scroll grew wide. Shadow on the left
                  edge tells the user there is more table underneath. */}
              <th className="sticky right-0 z-10 bg-slate-50 px-6 py-2 text-right font-medium shadow-[-6px_0_10px_-8px_rgba(15,23,42,0.15)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={visibleCols.length + 1} className="px-6 py-8 text-center">
                <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
              </td></tr>
            )}
            {!loading && sortedResumes.length === 0 && (
              <tr><td colSpan={visibleCols.length + 1} className="px-6 py-10 text-center text-sm text-slate-500"
                       data-testid="resumes-empty">
                {search ? "No resumes match this search." : "No resumes yet. Click \u201cUpload resume\u201d to add the first one."}
              </td></tr>
            )}
            {!loading && sortedResumes.map((r) => {
              const isCommentsOpen = !!openComments?.[r.resume_id];
              const isRejected = r.status === "rejected";
              return (
                <React.Fragment key={r.resume_id}>
                <tr className={`border-b border-slate-50 hover:bg-slate-50/50 ${isRejected ? "opacity-60" : ""}`}
                    data-testid={`resume-row-${r.resume_id}`}>
                  {visibleCols.map((c) => (
                    <td key={c.key} className={`px-3 py-3 align-top ${c.align === "center" ? "text-center" : c.align === "right" ? "text-right" : ""}`}>
                      {renderCell(c.key, r)}
                    </td>
                  ))}
                  <td className="sticky right-0 z-10 bg-white px-6 py-3 align-top text-right shadow-[-6px_0_10px_-8px_rgba(15,23,42,0.15)]">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline"
                              className={`h-7 px-2 ${isCommentsOpen ? "text-primary bg-indigo-50 border-primary/40" : ""}`}
                              onClick={() => onToggleComments(r.resume_id)}
                              data-testid={`resume-comments-toggle-${r.resume_id}`}
                              title="Comments">
                        <MessageSquare className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2"
                              onClick={() => onEditResume(r)}
                              data-testid={`resume-edit-${r.resume_id}`}
                              title="Edit candidate details">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2"
                              onClick={() => onDownload(r)}
                              data-testid={`resume-download-${r.resume_id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      {isRejected ? (
                        <Button size="sm" variant="outline"
                                className="h-7 px-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 border-emerald-200"
                                onClick={() => onRestore(r)}
                                data-testid={`resume-restore-${r.resume_id}`}
                                title="Restore to pool">
                          <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline"
                                className="h-7 px-2 text-amber-700 hover:text-amber-800 hover:bg-amber-50 border-amber-200"
                                onClick={() => onReject(r)}
                                data-testid={`resume-reject-${r.resume_id}`}
                                title="Reject (hides from default view)">
                          <XCircle className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline"
                              className="h-7 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              onClick={() => onDelete(r)}
                              data-testid={`resume-delete-${r.resume_id}`}
                              title="Permanently delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
                {isCommentsOpen && (
                  <tr className="border-b border-slate-100 bg-slate-50/40"
                      data-testid={`resume-comments-row-${r.resume_id}`}>
                    <td colSpan={visibleCols.length + 1} className="px-6 py-3">
                      <CommentsPanel resumeId={r.resume_id} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Silence "unused prop" lint warnings for onSendPark + parkBusy — they are
// wired through to a future bulk-action row that the parent may enable
// per-resume without re-signing the component.
void 0 && ({ onSendPark: null, parkBusy: null });
